import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import {
  Truck,
  Clock,
  Shield,
  Globe,
  Package,
  MapPin,
  Route,
  Warehouse,
  CheckCircle,
  ArrowRight,
  Phone,
  Mail,
  MessageCircle,
  ChevronDown,
  ChevronUp,
  Star,
  Zap,
  Target,
  Award,
  Send,
  Navigation,
  Timer,
  FileCheck,
  Container
} from 'lucide-react';

// Animated counter component
const AnimatedCounter = ({ end, suffix = '', prefix = '', duration = 2000, isVisible }) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!isVisible) return;
    let startTime;
    let animationFrame;

    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      setCount(Math.floor(easeOutQuart * end));
      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };

    animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
  }, [end, duration, isVisible]);

  return <span>{prefix}{count}{suffix}</span>;
};

// FAQ Accordion Item
const FAQItem = ({ question, answer, isOpen, onClick }) => (
  <div className="border-b border-gray-200 dark:border-gray-700">
    <button
      onClick={onClick}
      className="w-full py-5 flex items-center justify-between text-left hover:text-[#3ECDA3] transition-colors"
    >
      <span className="font-medium text-gray-900 dark:text-white pr-8">{question}</span>
      {isOpen ? (
        <ChevronUp className="w-5 h-5 text-[#3ECDA3] flex-shrink-0" />
      ) : (
        <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
      )}
    </button>
    <div className={`overflow-hidden transition-all duration-300 ${isOpen ? 'max-h-96 pb-5' : 'max-h-0'}`}>
      <p className="text-gray-600 dark:text-gray-400 leading-relaxed">{answer}</p>
    </div>
  </div>
);

const RoadTransportPage = () => {
  const { language } = useLanguage();
  const [openFAQ, setOpenFAQ] = useState(0);
  const [statsVisible, setStatsVisible] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    origin: '',
    destination: '',
    cargoType: '',
    vehicleType: '',
    weight: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const statsRef = useRef(null);
  const formRef = useRef(null);

  // Scroll to top on page load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const content = {
    pt: {
      hero: {
        badge: 'TRANSPORTE RODOVIÁRIO',
        title: 'Flexibilidade e Agilidade',
        titleHighlight: 'em Todo Território',
        subtitle: 'Conexão eficiente entre portos, aeroportos e destinos finais. Cobertura completa no Brasil e Mercosul com rastreamento via GPS.',
        cta: 'SOLICITAR COTAÇÃO',
        ctaSecondary: 'FALAR COM ESPECIALISTA'
      },
      benefits: {
        title: 'Vantagens do',
        titleHighlight: 'Transporte Rodoviário',
        items: [
          { icon: Zap, title: 'Agilidade', desc: 'Coleta em até 24h e entregas expressas em todo Brasil' },
          { icon: Route, title: 'Flexibilidade', desc: 'Rotas personalizadas e adaptação às suas necessidades' },
          { icon: Navigation, title: 'Rastreamento GPS', desc: 'Acompanhe sua carga durante todo o trajeto' },
          { icon: Shield, title: 'Segurança', desc: 'Frota rastreada, seguro completo e escoltas quando necessário' },
          { icon: Target, title: 'Porta a Porta', desc: 'Entrega direta do remetente ao destinatário final' },
          { icon: Award, title: 'Expertise', desc: 'Rede de transportadoras homologadas em todo país' }
        ]
      },
      services: {
        title: 'Modalidades de',
        titleHighlight: 'Transporte',
        items: [
          { 
            icon: Container, 
            name: 'Transporte de Contêineres', 
            desc: 'Movimentação de contêineres entre portos, terminais e destinos finais.',
            details: ['Contêineres 20\' e 40\'', 'Remoção portuária', 'Entrega em planta', 'Devolução de vazio']
          },
          { 
            icon: Package, 
            name: 'Carga Fracionada (LTL)', 
            desc: 'Ideal para volumes menores. Pague apenas pelo espaço utilizado.',
            details: ['Coletas programadas', 'Consolidação eficiente', 'Entregas em todo Brasil', 'Rastreio por NF']
          },
          { 
            icon: Truck, 
            name: 'Carga Completa (FTL)', 
            desc: 'Veículo dedicado exclusivamente para sua carga.',
            details: ['Caminhões de todos os portes', 'Carreta fechada ou sider', 'Prancha e plataforma', 'Carga refrigerada']
          },
          { 
            icon: FileCheck, 
            name: 'DTA e MIC-DTA', 
            desc: 'Trânsito aduaneiro entre recintos alfandegados.',
            details: ['Porto a porto seco', 'Aeroporto a EADI', 'Documentação completa', 'Acompanhamento fiscal']
          },
        ]
      },
      stats: {
        title: 'Nossa Operação',
        titleHighlight: 'Rodoviária',
        items: [
          { value: 27, suffix: '', label: 'Estados' },
          { value: 24, suffix: 'h', label: 'Coleta' },
          { value: 99, suffix: '%', label: 'On-time' }
        ]
      },
      coverage: {
        title: 'Cobertura',
        titleHighlight: 'Nacional',
        regions: [
          { name: 'Sul', states: 'RS, SC, PR', highlight: 'Conexão Mercosul' },
          { name: 'Sudeste', states: 'SP, RJ, MG, ES', highlight: 'Principais portos' },
          { name: 'Centro-Oeste', states: 'GO, MT, MS, DF', highlight: 'Agronegócio' },
          { name: 'Nordeste', states: 'BA, PE, CE e outros', highlight: 'Rotas consolidadas' },
          { name: 'Norte', states: 'AM, PA e outros', highlight: 'Logística especial' },
        ]
      },
      process: {
        title: 'Como Funciona',
        steps: [
          { num: '01', title: 'Cotação', desc: 'Análise da rota e proposta em até 24 horas' },
          { num: '02', title: 'Coleta', desc: 'Retirada da carga em até 24 horas' },
          { num: '03', title: 'Transporte', desc: 'Monitoramento GPS durante o trajeto' },
          { num: '04', title: 'Entrega', desc: 'Confirmação com foto e assinatura' },
        ]
      },
      vehicles: {
        title: 'Nossa',
        titleHighlight: 'Frota',
        items: [
          { name: 'Fiorino/Van', capacity: 'Até 1.500kg', use: 'Entregas urbanas' },
          { name: 'VUC/3/4', capacity: 'Até 3.000kg', use: 'Carga fracionada' },
          { name: 'Toco', capacity: 'Até 6.000kg', use: 'Média distância' },
          { name: 'Truck', capacity: 'Até 14.000kg', use: 'Carga geral' },
          { name: 'Carreta', capacity: 'Até 28.000kg', use: 'Grandes volumes' },
          { name: 'Bitrem', capacity: 'Até 40.000kg', use: 'Cargas pesadas' },
        ]
      },
      testimonial: {
        quote: 'A Well Cargo revolucionou nossa logística de distribuição. Coletas pontuais, rastreamento eficiente e zero avarias. Parceria que funciona!',
        author: 'Fernanda Lima',
        role: 'Coordenadora de Logística',
        company: 'Distribuidora Nacional'
      },
      faq: {
        title: 'Perguntas',
        titleHighlight: 'Frequentes',
        items: [
          { q: 'Qual o prazo para coleta da carga?', a: 'Realizamos coletas em até 24 horas após a confirmação do pedido na maioria das cidades. Para regiões metropolitanas de SP, RJ, BH e Vitória, oferecemos coleta no mesmo dia para pedidos até 12h.' },
          { q: 'Vocês transportam cargas perigosas?', a: 'Sim! Somos especializados no transporte de cargas perigosas (produtos químicos, inflamáveis, corrosivos) com motoristas habilitados, veículos adequados e toda documentação exigida pela ANTT.' },
          { q: 'Como funciona o rastreamento?', a: 'Todas as nossas cargas são rastreadas via GPS. Você recebe um link de acompanhamento e pode visualizar a posição do veículo, previsão de chegada e histórico de movimentação.' },
          { q: 'Vocês fazem transporte internacional rodoviário?', a: 'Sim! Operamos rotas para todo Mercosul (Argentina, Uruguai, Paraguai e Chile) com documentação MIC-DTA, seguro internacional e parceiros locais para entrega final.' },
          { q: 'Qual o seguro das cargas transportadas?', a: 'Todas as cargas viajam com seguro incluso (RC Transportador). Para cargas de maior valor, oferecemos seguro adicional (Ad Valorem) com cobertura completa contra roubo, avarias e acidentes.' }
        ]
      },
      cta: {
        title: 'Pronto para Agilizar',
        titleHighlight: 'suas Entregas?',
        subtitle: 'Preencha o formulário e receba uma cotação personalizada em até 24 horas.',
        badge: 'RESPOSTA RÁPIDA'
      },
      form: {
        name: 'Nome completo',
        email: 'E-mail',
        phone: 'WhatsApp',
        company: 'Empresa',
        origin: 'Cidade de origem',
        destination: 'Cidade de destino',
        cargoType: 'Tipo de carga',
        vehicleType: 'Tipo de veículo',
        weight: 'Peso estimado (kg)',
        message: 'Detalhes adicionais (opcional)',
        submit: 'SOLICITAR COTAÇÃO GRÁTIS',
        success: 'Recebemos sua solicitação! Nossa equipe entrará em contato em breve.',
        vehicleOptions: ['Fiorino/Van', 'VUC/3/4', 'Toco', 'Truck', 'Carreta Fechada', 'Carreta Sider', 'Prancha', 'Refrigerado'],
        cargoOptions: ['Carga Geral', 'Contêiner', 'Carga Fracionada', 'Carga Perigosa', 'Carga Refrigerada', 'Máquinas/Equipamentos', 'Mudança', 'Outros']
      },
      contact: {
        title: 'Prefere falar diretamente?',
        phone: '+55 27 3534-2660',
        whatsapp: '+55 31 99592-5853',
        email: 'rodoviario@wellcargo.com.br'
      }
    },
    en: {
      hero: {
        badge: 'ROAD TRANSPORT',
        title: 'Flexibility and Agility',
        titleHighlight: 'Across All Territory',
        subtitle: 'Efficient connection between ports, airports and final destinations. Complete coverage in Brazil and Mercosur with GPS tracking.',
        cta: 'REQUEST QUOTE',
        ctaSecondary: 'TALK TO SPECIALIST'
      },
      benefits: {
        title: 'Advantages of',
        titleHighlight: 'Road Transport',
        items: [
          { icon: Zap, title: 'Agility', desc: 'Pickup within 24h and express deliveries throughout Brazil' },
          { icon: Route, title: 'Flexibility', desc: 'Customized routes adapted to your needs' },
          { icon: Navigation, title: 'GPS Tracking', desc: 'Track your cargo throughout the journey' },
          { icon: Shield, title: 'Security', desc: 'Tracked fleet, full insurance and escorts when needed' },
          { icon: Target, title: 'Door to Door', desc: 'Direct delivery from sender to final recipient' },
          { icon: Award, title: 'Expertise', desc: 'Network of approved carriers nationwide' }
        ]
      },
      services: {
        title: 'Transport',
        titleHighlight: 'Modalities',
        items: [
          { 
            icon: Container, 
            name: 'Container Transport', 
            desc: 'Container movement between ports, terminals and final destinations.',
            details: ['20\' and 40\' containers', 'Port removal', 'Plant delivery', 'Empty return']
          },
          { 
            icon: Package, 
            name: 'Less Than Truckload (LTL)', 
            desc: 'Ideal for smaller volumes. Pay only for the space used.',
            details: ['Scheduled pickups', 'Efficient consolidation', 'Deliveries throughout Brazil', 'Invoice tracking']
          },
          { 
            icon: Truck, 
            name: 'Full Truckload (FTL)', 
            desc: 'Vehicle dedicated exclusively to your cargo.',
            details: ['Trucks of all sizes', 'Closed or sider trailer', 'Flatbed and platform', 'Refrigerated cargo']
          },
          { 
            icon: FileCheck, 
            name: 'DTA and MIC-DTA', 
            desc: 'Customs transit between bonded warehouses.',
            details: ['Port to dry port', 'Airport to EADI', 'Complete documentation', 'Fiscal monitoring']
          },
        ]
      },
      stats: {
        title: 'Our Road',
        titleHighlight: 'Operation',
        items: [
          { value: 27, suffix: '', label: 'States' },
          { value: 24, suffix: 'h', label: 'Pickup' },
          { value: 99, suffix: '%', label: 'On-time' }
        ]
      },
      coverage: {
        title: 'National',
        titleHighlight: 'Coverage',
        regions: [
          { name: 'South', states: 'RS, SC, PR', highlight: 'Mercosur Connection' },
          { name: 'Southeast', states: 'SP, RJ, MG, ES', highlight: 'Main ports' },
          { name: 'Midwest', states: 'GO, MT, MS, DF', highlight: 'Agribusiness' },
          { name: 'Northeast', states: 'BA, PE, CE and others', highlight: 'Consolidated routes' },
          { name: 'North', states: 'AM, PA and others', highlight: 'Special logistics' },
        ]
      },
      process: {
        title: 'How it Works',
        steps: [
          { num: '01', title: 'Quote', desc: 'Route analysis and proposal within 24 hours' },
          { num: '02', title: 'Pickup', desc: 'Cargo collection within 24 hours' },
          { num: '03', title: 'Transport', desc: 'Real-time GPS monitoring' },
          { num: '04', title: 'Delivery', desc: 'Confirmation with photo and signature' },
        ]
      },
      vehicles: {
        title: 'Our',
        titleHighlight: 'Fleet',
        items: [
          { name: 'Van', capacity: 'Up to 1,500kg', use: 'Urban deliveries' },
          { name: 'Small Truck', capacity: 'Up to 3,000kg', use: 'Fractional cargo' },
          { name: 'Medium Truck', capacity: 'Up to 6,000kg', use: 'Medium distance' },
          { name: 'Large Truck', capacity: 'Up to 14,000kg', use: 'General cargo' },
          { name: 'Trailer', capacity: 'Up to 28,000kg', use: 'Large volumes' },
          { name: 'Road Train', capacity: 'Up to 40,000kg', use: 'Heavy loads' },
        ]
      },
      testimonial: {
        quote: 'Well Cargo revolutionized our distribution logistics. Punctual pickups, efficient tracking and zero damages. A partnership that works!',
        author: 'Fernanda Lima',
        role: 'Logistics Coordinator',
        company: 'National Distributor'
      },
      faq: {
        title: 'Frequently Asked',
        titleHighlight: 'Questions',
        items: [
          { q: 'What is the pickup deadline?', a: 'We pick up within 24 hours after order confirmation in most cities. For metropolitan regions of SP, RJ, BH and Vitória, we offer same-day pickup for orders until noon.' },
          { q: 'Do you transport dangerous goods?', a: 'Yes! We specialize in transporting hazardous cargo (chemicals, flammables, corrosives) with licensed drivers, adequate vehicles and all documentation required by ANTT.' },
          { q: 'How does tracking work?', a: 'All our cargo is tracked via GPS. You receive a tracking link and can view the position of the vehicle, arrival forecast and movement history.' },
          { q: 'Do you do international road transport?', a: 'Yes! We operate routes to all Mercosur (Argentina, Uruguay, Paraguay and Chile) with MIC-DTA documentation, international insurance and local partners for final delivery.' },
          { q: 'What is the insurance for transported cargo?', a: 'All cargo travels with included insurance (Carrier RC). For higher value cargo, we offer additional insurance (Ad Valorem) with full coverage against theft, damage and accidents.' }
        ]
      },
      cta: {
        title: 'Ready to Speed Up',
        titleHighlight: 'your Deliveries?',
        subtitle: 'Fill out the form and receive a personalized quote within 24 hours.',
        badge: 'FAST RESPONSE'
      },
      form: {
        name: 'Full name',
        email: 'Email',
        phone: 'WhatsApp',
        company: 'Company',
        origin: 'Origin city',
        destination: 'Destination city',
        cargoType: 'Cargo type',
        vehicleType: 'Vehicle type',
        weight: 'Estimated weight (kg)',
        message: 'Additional details (optional)',
        submit: 'REQUEST FREE QUOTE',
        success: 'We received your request! Our team will contact you soon.',
        vehicleOptions: ['Van', 'Small Truck', 'Medium Truck', 'Large Truck', 'Closed Trailer', 'Sider Trailer', 'Flatbed', 'Refrigerated'],
        cargoOptions: ['General Cargo', 'Container', 'Fractional Cargo', 'Hazardous Cargo', 'Refrigerated Cargo', 'Machines/Equipment', 'Moving', 'Others']
      },
      contact: {
        title: 'Prefer to talk directly?',
        phone: '+55 27 3534-2660',
        whatsapp: '+55 31 99592-5853',
        email: 'rodoviario@wellcargo.com.br'
      }
    },
    es: {
      hero: {
        badge: 'TRANSPORTE TERRESTRE',
        title: 'Flexibilidad y Agilidad',
        titleHighlight: 'en Todo el Territorio',
        subtitle: 'Conexión eficiente entre puertos, aeropuertos y destinos finales. Cobertura completa en Brasil y Mercosur con rastreo GPS.',
        cta: 'SOLICITAR COTIZACIÓN',
        ctaSecondary: 'HABLAR CON ESPECIALISTA'
      },
      benefits: {
        title: 'Ventajas del',
        titleHighlight: 'Transporte Terrestre',
        items: [
          { icon: Zap, title: 'Agilidad', desc: 'Recogida en 24h y entregas express en todo Brasil' },
          { icon: Route, title: 'Flexibilidad', desc: 'Rutas personalizadas adaptadas a sus necesidades' },
          { icon: Navigation, title: 'Rastreo GPS', desc: 'Siga su carga en tiempo real las 24 horas' },
          { icon: Shield, title: 'Seguridad', desc: 'Flota rastreada, seguro completo y escoltas cuando sea necesario' },
          { icon: Target, title: 'Puerta a Puerta', desc: 'Entrega directa del remitente al destinatario final' },
          { icon: Award, title: 'Experiencia', desc: 'Red de transportistas homologados en todo el país' }
        ]
      },
      services: {
        title: 'Modalidades de',
        titleHighlight: 'Transporte',
        items: [
          { 
            icon: Container, 
            name: 'Transporte de Contenedores', 
            desc: 'Movimiento de contenedores entre puertos, terminales y destinos finales.',
            details: ['Contenedores 20\' y 40\'', 'Retiro portuario', 'Entrega en planta', 'Devolución de vacío']
          },
          { 
            icon: Package, 
            name: 'Carga Fraccionada (LTL)', 
            desc: 'Ideal para volúmenes menores. Pague solo por el espacio utilizado.',
            details: ['Recogidas programadas', 'Consolidación eficiente', 'Entregas en todo Brasil', 'Rastreo por factura']
          },
          { 
            icon: Truck, 
            name: 'Carga Completa (FTL)', 
            desc: 'Vehículo dedicado exclusivamente a su carga.',
            details: ['Camiones de todos los tamaños', 'Trailer cerrado o sider', 'Plataforma', 'Carga refrigerada']
          },
          { 
            icon: FileCheck, 
            name: 'DTA y MIC-DTA', 
            desc: 'Tránsito aduanero entre recintos.',
            details: ['Puerto a puerto seco', 'Aeropuerto a EADI', 'Documentación completa', 'Seguimiento fiscal']
          },
        ]
      },
      stats: {
        title: 'Nuestra Operación',
        titleHighlight: 'Terrestre',
        items: [
          { value: 27, suffix: '', label: 'Estados' },
          { value: 24, suffix: 'h', label: 'Recogida' },
          { value: 99, suffix: '%', label: 'A tiempo' }
        ]
      },
      coverage: {
        title: 'Cobertura',
        titleHighlight: 'Nacional',
        regions: [
          { name: 'Sur', states: 'RS, SC, PR', highlight: 'Conexión Mercosur' },
          { name: 'Sudeste', states: 'SP, RJ, MG, ES', highlight: 'Principales puertos' },
          { name: 'Centro-Oeste', states: 'GO, MT, MS, DF', highlight: 'Agronegocios' },
          { name: 'Nordeste', states: 'BA, PE, CE y otros', highlight: 'Rutas consolidadas' },
          { name: 'Norte', states: 'AM, PA y otros', highlight: 'Logística especial' },
        ]
      },
      process: {
        title: 'Cómo Funciona',
        steps: [
          { num: '01', title: 'Cotización', desc: 'Análisis de ruta y propuesta en 24 horas' },
          { num: '02', title: 'Recogida', desc: 'Retiro de carga en 24 horas' },
          { num: '03', title: 'Transporte', desc: 'Monitoreo GPS en tiempo real' },
          { num: '04', title: 'Entrega', desc: 'Confirmación con foto y firma' },
        ]
      },
      vehicles: {
        title: 'Nuestra',
        titleHighlight: 'Flota',
        items: [
          { name: 'Furgoneta', capacity: 'Hasta 1.500kg', use: 'Entregas urbanas' },
          { name: 'Camión pequeño', capacity: 'Hasta 3.000kg', use: 'Carga fraccionada' },
          { name: 'Camión mediano', capacity: 'Hasta 6.000kg', use: 'Media distancia' },
          { name: 'Camión grande', capacity: 'Hasta 14.000kg', use: 'Carga general' },
          { name: 'Trailer', capacity: 'Hasta 28.000kg', use: 'Grandes volúmenes' },
          { name: 'Bitren', capacity: 'Hasta 40.000kg', use: 'Cargas pesadas' },
        ]
      },
      testimonial: {
        quote: 'Well Cargo revolucionó nuestra logística de distribución. Recogidas puntuales, rastreo eficiente y cero daños. ¡Una asociación que funciona!',
        author: 'Fernanda Lima',
        role: 'Coordinadora de Logística',
        company: 'Distribuidora Nacional'
      },
      faq: {
        title: 'Preguntas',
        titleHighlight: 'Frecuentes',
        items: [
          { q: '¿Cuál es el plazo de recogida?', a: 'Recogemos en 24 horas después de la confirmación del pedido en la mayoría de las ciudades. Para regiones metropolitanas de SP, RJ, BH y Vitória, ofrecemos recogida el mismo día para pedidos hasta las 12h.' },
          { q: '¿Transportan cargas peligrosas?', a: '¡Sí! Nos especializamos en transporte de cargas peligrosas (químicos, inflamables, corrosivos) con conductores habilitados, vehículos adecuados y toda la documentación requerida.' },
          { q: '¿Cómo funciona el rastreo?', a: 'Todas nuestras cargas son rastreadas via GPS. Recibe un enlace de seguimiento y puede ver la posición del vehículo y la previsión de llegada.' },
          { q: '¿Hacen transporte internacional terrestre?', a: '¡Sí! Operamos rutas a todo Mercosur (Argentina, Uruguay, Paraguay y Chile) con documentación MIC-DTA, seguro internacional y socios locales.' },
          { q: '¿Cuál es el seguro de las cargas?', a: 'Todas las cargas viajan con seguro incluido. Para cargas de mayor valor, ofrecemos seguro adicional con cobertura completa contra robo, daños y accidentes.' }
        ]
      },
      cta: {
        title: '¿Listo para Agilizar',
        titleHighlight: 'sus Entregas?',
        subtitle: 'Complete el formulario y reciba una cotización personalizada en 24 horas.',
        badge: 'RESPUESTA RÁPIDA'
      },
      form: {
        name: 'Nombre completo',
        email: 'Correo electrónico',
        phone: 'WhatsApp',
        company: 'Empresa',
        origin: 'Ciudad de origen',
        destination: 'Ciudad de destino',
        cargoType: 'Tipo de carga',
        vehicleType: 'Tipo de vehículo',
        weight: 'Peso estimado (kg)',
        message: 'Detalles adicionales (opcional)',
        submit: 'SOLICITAR COTIZACIÓN GRATIS',
        success: '¡Recibimos su solicitud! Nuestro equipo lo contactará pronto.',
        vehicleOptions: ['Furgoneta', 'Camión pequeño', 'Camión mediano', 'Camión grande', 'Trailer cerrado', 'Trailer sider', 'Plataforma', 'Refrigerado'],
        cargoOptions: ['Carga General', 'Contenedor', 'Carga Fraccionada', 'Carga Peligrosa', 'Carga Refrigerada', 'Máquinas/Equipos', 'Mudanza', 'Otros']
      },
      contact: {
        title: '¿Prefiere hablar directamente?',
        phone: '+55 27 3534-2660',
        whatsapp: '+55 31 99592-5853',
        email: 'rodoviario@wellcargo.com.br'
      }
    }
  };

  const c = content[language];

  // Intersection observer for stats
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setStatsVisible(true);
      },
      { threshold: 0.3 }
    );
    if (statsRef.current) observer.observe(statsRef.current);
    return () => observer.disconnect();
  }, []);

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setTimeout(() => {
      console.log('Road transport lead form submitted:', formData);
      setIsSubmitting(false);
      setSubmitted(true);
      setFormData({ name: '', email: '', phone: '', company: '', origin: '', destination: '', cargoType: '', vehicleType: '', weight: '', message: '' });
    }, 1500);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 transition-colors duration-300">
      <Header />

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: "url('https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?w=1920&q=80')" }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-emerald-900/90 via-emerald-900/70 to-transparent" />
        </div>

        {/* Road pattern decoration */}
        <div className="absolute bottom-0 left-0 right-0 h-20 overflow-hidden opacity-20">
          <div className="absolute bottom-0 left-0 right-0 h-4 bg-gray-800 flex items-center justify-center">
            <div className="flex gap-8">
              {[...Array(20)].map((_, i) => (
                <div key={i} className="w-16 h-1 bg-yellow-400"></div>
              ))}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 backdrop-blur-sm rounded-full px-4 py-2 mb-6 animate-fade-in">
              <Truck className="w-4 h-4 text-emerald-400" />
              <span className="text-emerald-400 text-sm font-medium">{c.hero.badge}</span>
            </div>

            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-tight mb-6 animate-fade-in" style={{ animationDelay: '0.2s' }}>
              {c.hero.title}<br />
              <span className="text-emerald-400 italic">{c.hero.titleHighlight}</span>
            </h1>

            <p className="text-gray-300 text-lg md:text-xl mb-8 leading-relaxed animate-fade-in" style={{ animationDelay: '0.4s' }}>
              {c.hero.subtitle}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 animate-fade-in" style={{ animationDelay: '0.6s' }}>
              <button
                onClick={scrollToForm}
                className="group bg-emerald-500 hover:bg-emerald-600 text-white px-8 py-4 rounded-lg font-semibold flex items-center justify-center gap-2 transition-all duration-300 hover:shadow-lg hover:shadow-emerald-500/30"
              >
                {c.hero.cta}
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
              <a
                href="https://wa.me/5531995925853"
                target="_blank"
                rel="noopener noreferrer"
                className="group border-2 border-white/30 hover:border-emerald-400 text-white px-8 py-4 rounded-lg font-semibold flex items-center justify-center gap-2 transition-all duration-300 hover:bg-emerald-500/10"
              >
                <MessageCircle className="w-5 h-5" />
                {c.hero.ctaSecondary}
              </a>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <button onClick={scrollToForm} className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white animate-bounce">
          <ChevronDown size={32} />
        </button>
      </section>

      {/* Benefits Section */}
      <section className="py-20 md:py-28 bg-gray-50 dark:bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white">
              {c.benefits.title} <span className="text-emerald-500 italic">{c.benefits.titleHighlight}</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {c.benefits.items.map((item, index) => {
              const IconComp = item.icon;
              return (
                <div
                  key={index}
                  className="group bg-white dark:bg-gray-900 rounded-2xl p-8 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border border-gray-100 dark:border-gray-700"
                >
                  <div className="w-14 h-14 bg-emerald-500/10 rounded-xl flex items-center justify-center mb-6 group-hover:bg-emerald-500 transition-colors duration-300">
                    <IconComp className="w-7 h-7 text-emerald-500 group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">{item.title}</h3>
                  <p className="text-gray-600 dark:text-gray-400">{item.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 md:py-28 bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white">
              {c.services.title} <span className="text-emerald-500 italic">{c.services.titleHighlight}</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {c.services.items.map((item, index) => {
              const IconComp = item.icon;
              return (
                <div key={index} className="group bg-gradient-to-br from-gray-50 to-white dark:from-gray-800 dark:to-gray-900 rounded-2xl p-8 border border-gray-200 dark:border-gray-700 hover:border-emerald-500/30 hover:shadow-xl transition-all duration-300">
                  <div className="flex items-start gap-6">
                    <div className="w-16 h-16 bg-emerald-500 rounded-2xl flex items-center justify-center flex-shrink-0">
                      <IconComp className="w-8 h-8 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">{item.name}</h3>
                      <p className="text-gray-600 dark:text-gray-400 mb-4">{item.desc}</p>
                      <div className="flex flex-wrap gap-2">
                        {item.details.map((detail, i) => (
                          <span key={i} className="inline-flex items-center gap-1 text-xs bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 px-3 py-1 rounded-full">
                            <CheckCircle className="w-3 h-3" />
                            {detail}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section ref={statsRef} className="py-20 bg-gradient-to-r from-emerald-800 to-teal-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white">
              {c.stats.title} <span className="italic text-emerald-300">{c.stats.titleHighlight}</span>
            </h2>
          </div>
          <div className="grid grid-cols-3 gap-8 max-w-3xl mx-auto">
            {c.stats.items.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-white mb-2">
                  <AnimatedCounter end={stat.value} suffix={stat.suffix} isVisible={statsVisible} />
                </div>
                <p className="text-emerald-300 text-sm uppercase tracking-wider">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Coverage Section */}
      <section className="py-20 md:py-28 bg-gray-50 dark:bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white">
              {c.coverage.title} <span className="text-emerald-500 italic">{c.coverage.titleHighlight}</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
            {c.coverage.regions.map((region, index) => (
              <div key={index} className="bg-white dark:bg-gray-900 rounded-2xl p-6 border border-gray-200 dark:border-gray-700 hover:border-emerald-500/30 transition-all duration-300 text-center">
                <MapPin className="w-8 h-8 text-emerald-500 mx-auto mb-3" />
                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-1">{region.name}</h3>
                <p className="text-gray-500 dark:text-gray-400 text-sm mb-2">{region.states}</p>
                <span className="inline-block text-xs bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 px-3 py-1 rounded-full">
                  {region.highlight}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 md:py-28 bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white text-center mb-16">
            {c.process.title}
          </h2>
          <div className="relative">
            <div className="hidden md:block absolute top-1/2 left-0 right-0 h-1 bg-emerald-500/20 -translate-y-1/2" />
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              {c.process.steps.map((step, index) => (
                <div key={index} className="relative text-center group">
                  <div className="w-16 h-16 mx-auto bg-emerald-500 rounded-full flex items-center justify-center mb-4 relative z-10 group-hover:scale-105 transition-transform shadow-lg">
                    <span className="text-white text-xl font-bold">{step.num}</span>
                  </div>
                  <h4 className="font-bold text-gray-900 dark:text-white mb-2">{step.title}</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="py-20 bg-gradient-to-br from-emerald-800 to-teal-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex justify-center gap-1 mb-6">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="w-6 h-6 fill-emerald-300 text-emerald-300" />
            ))}
          </div>
          <blockquote className="text-2xl md:text-3xl text-white italic leading-relaxed mb-8">
            "{c.testimonial.quote}"
          </blockquote>
          <div>
            <p className="font-bold text-white">{c.testimonial.author}</p>
            <p className="text-emerald-300">{c.testimonial.role}, {c.testimonial.company}</p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 md:py-28 bg-white dark:bg-gray-900">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white text-center mb-12">
            {c.faq.title} <span className="text-emerald-500 italic">{c.faq.titleHighlight}</span>
          </h2>
          <div className="bg-gray-50 dark:bg-gray-800 rounded-2xl shadow-sm p-6 md:p-8">
            {c.faq.items.map((item, index) => (
              <FAQItem
                key={index}
                question={item.q}
                answer={item.a}
                isOpen={openFAQ === index}
                onClick={() => setOpenFAQ(openFAQ === index ? -1 : index)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* CTA & Form Section */}
      <section ref={formRef} className="py-20 md:py-28 bg-gradient-to-br from-emerald-800 to-teal-700 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-emerald-300 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Left - CTA Text */}
            <div className="text-center lg:text-left">
              <div className="inline-flex items-center gap-2 bg-white/10 rounded-full px-4 py-2 mb-6">
                <Zap className="w-4 h-4 text-emerald-300" />
                <span className="text-emerald-300 text-sm font-medium">{c.cta.badge}</span>
              </div>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight mb-6">
                {c.cta.title}<br />
                <span className="text-emerald-300 italic">{c.cta.titleHighlight}</span>
              </h2>
              <p className="text-gray-300 text-lg mb-8">{c.cta.subtitle}</p>

              <div className="space-y-4">
                <p className="text-white/70 text-sm">{c.contact.title}</p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                  <a href={`tel:${c.contact.phone}`} className="flex items-center gap-2 text-white hover:text-emerald-300 transition-colors">
                    <Phone className="w-5 h-5" />
                    <span>{c.contact.phone}</span>
                  </a>
                  <a href={`https://wa.me/${c.contact.whatsapp.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-white hover:text-emerald-300 transition-colors">
                    <MessageCircle className="w-5 h-5" />
                    <span>WhatsApp</span>
                  </a>
                </div>
              </div>
            </div>

            {/* Right - Form */}
            <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl p-8">
              {submitted ? (
                <div className="text-center py-12">
                  <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Recebemos!</h3>
                  <p className="text-gray-600 dark:text-gray-400">{c.form.success}</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <input
                      type="text" name="name" required placeholder={c.form.name} value={formData.name} onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent dark:bg-gray-800 dark:text-white outline-none"
                    />
                    <input
                      type="email" name="email" required placeholder={c.form.email} value={formData.email} onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent dark:bg-gray-800 dark:text-white outline-none"
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <input
                      type="tel" name="phone" required placeholder={c.form.phone} value={formData.phone} onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent dark:bg-gray-800 dark:text-white outline-none"
                    />
                    <input
                      type="text" name="company" placeholder={c.form.company} value={formData.company} onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent dark:bg-gray-800 dark:text-white outline-none"
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <input
                      type="text" name="origin" required placeholder={c.form.origin} value={formData.origin} onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent dark:bg-gray-800 dark:text-white outline-none"
                    />
                    <input
                      type="text" name="destination" required placeholder={c.form.destination} value={formData.destination} onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent dark:bg-gray-800 dark:text-white outline-none"
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <select
                      name="vehicleType" required value={formData.vehicleType} onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent dark:bg-gray-800 dark:text-white outline-none"
                    >
                      <option value="">{c.form.vehicleType}</option>
                      {c.form.vehicleOptions.map((opt, i) => <option key={i} value={opt}>{opt}</option>)}
                    </select>
                    <select
                      name="cargoType" required value={formData.cargoType} onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent dark:bg-gray-800 dark:text-white outline-none"
                    >
                      <option value="">{c.form.cargoType}</option>
                      {c.form.cargoOptions.map((opt, i) => <option key={i} value={opt}>{opt}</option>)}
                    </select>
                  </div>
                  <input
                    type="text" name="weight" placeholder={c.form.weight} value={formData.weight} onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent dark:bg-gray-800 dark:text-white outline-none"
                  />
                  <textarea
                    name="message" rows={3} placeholder={c.form.message} value={formData.message} onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent dark:bg-gray-800 dark:text-white outline-none resize-none"
                  />
                  <button
                    type="submit" disabled={isSubmitting}
                    className="w-full bg-emerald-500 hover:bg-emerald-600 text-white py-4 rounded-lg font-semibold flex items-center justify-center gap-2 transition-all duration-300 disabled:opacity-50"
                  >
                    {isSubmitting ? (
                      <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        {c.form.submit}
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default RoadTransportPage;
